import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, FileText, Download, Eye } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { formatDate } from '../../utils/dateFormatter';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EPaperManagement = () => {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [epapers, setEpapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    date: '',
    pdf_url: '',
    thumbnail_url: '',
  });

  useEffect(() => {
    fetchEpapers();
  }, []);

  const fetchEpapers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/epapers`, getAuthHeaders());
      setEpapers(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch e-papers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handlePDFUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/upload/pdf`,
        uploadFormData,
        {
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setFormData({ ...formData, pdf_url: response.data.url });
      toast({ title: 'Success', description: 'PDF uploaded successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'PDF upload failed', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('category', 'epapers');

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/upload/image`,
        uploadFormData,
        {
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setFormData({ ...formData, thumbnail_url: response.data.url });
      toast({ title: 'Success', description: 'Thumbnail uploaded successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Thumbnail upload failed', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/admin/epapers`, formData, getAuthHeaders());
      toast({ title: 'Success', description: 'E-Paper uploaded successfully' });
      resetForm();
      fetchEpapers();
      setShowModal(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to upload e-paper', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this e-paper?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/epapers/${id}`, getAuthHeaders());
      toast({ title: 'Success', description: 'E-Paper deleted successfully' });
      fetchEpapers();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete e-paper', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      pdf_url: '',
      thumbnail_url: '',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">E-Paper Management</h1>
          <p className="text-gray-600 mt-1">Upload and manage daily e-papers</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-red-600 hover:bg-red-700">
          <Plus className="w-4 h-4 mr-2" />
          Upload E-Paper
        </Button>
      </div>

      {/* E-Papers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {loading && epapers.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          </div>
        ) : epapers.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No e-papers uploaded yet. Upload your first e-paper!</p>
            </CardContent>
          </Card>
        ) : (
          epapers.map((epaper) => (
            <Card key={epaper.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="relative h-64 mb-3 bg-gray-100 rounded-lg overflow-hidden">
                  {epaper.thumbnail_url ? (
                    <img
                      src={epaper.thumbnail_url}
                      alt={epaper.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <FileText className="w-16 h-16 text-gray-300" />
                    </div>
                  )}
                </div>
                <h3 className="font-semibold mb-2 line-clamp-2">{epaper.title}</h3>
                <p className="text-sm text-gray-500 mb-3">{formatDate(epaper.date)}</p>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => window.open(epaper.pdf_url, '_blank')}>
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(epaper.pdf_url, '_blank')}>
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(epaper.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="mt-2 flex items-center text-xs text-gray-500">
                  <Eye className="w-3 h-3 mr-1" />
                  {epaper.views} views
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Upload Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload New E-Paper</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Tripura Darpan - March 15, 2026"
                required
              />
            </div>

            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>PDF File *</Label>
              <Input type="file" accept="application/pdf" onChange={handlePDFUpload} required={!formData.pdf_url} />
              {uploading && <p className="text-sm text-gray-500 mt-1">Uploading PDF...</p>}
              {formData.pdf_url && (
                <p className="text-sm text-green-600 mt-1">✓ PDF uploaded successfully</p>
              )}
            </div>

            <div>
              <Label>Thumbnail (Optional)</Label>
              <Input type="file" accept="image/*" onChange={handleThumbnailUpload} />
              {formData.thumbnail_url && (
                <img src={formData.thumbnail_url} alt="Thumbnail" className="mt-2 h-32 rounded" />
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700" disabled={loading || !formData.pdf_url}>
                {loading ? 'Uploading...' : 'Upload E-Paper'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EPaperManagement;